/* ============================================
   ACCESSIBILITY JAVASCRIPT
   WCAG 2.1 AA Compliance Features
   ============================================ */

(function() {
  'use strict';

  // Storage key prefix
  const STORAGE_PREFIX = 'ww1991_a11y_';

  // Screen reader announcer
  function announce(message, priority = 'polite') {
    const announcer = document.getElementById('a11y-announcer');
    if (!announcer) return;
    announcer.setAttribute('aria-live', priority);
    announcer.textContent = '';
    setTimeout(() => {
      announcer.textContent = message;
    }, 100);
  }

  // Save preference to localStorage
  function savePreference(key, value) {
    try {
      localStorage.setItem(STORAGE_PREFIX + key, value);
    } catch (e) {
      console.warn('Could not save accessibility preference:', e);
    }
  }

  // Load preference from localStorage
  function loadPreference(key) {
    try {
      return localStorage.getItem(STORAGE_PREFIX + key);
    } catch (e) {
      return null;
    }
  }

  // Accessibility Widget Toggle
  function initA11yWidget() {
    const toggle = document.getElementById('a11y-toggle');
    const widget = document.getElementById('a11y-widget');
    const close = document.getElementById('a11y-close');

    if (!toggle || !widget) return;

    function openWidget() {
      widget.classList.add('open');
      widget.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      // Don't auto-focus close button, let user continue clicking options
      announce('Accessibility options opened');
    }

    function closeWidget() {
      widget.classList.remove('open');
      widget.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
      announce('Accessibility options closed');
    }

    toggle.addEventListener('click', () => {
      if (widget.classList.contains('open')) {
        closeWidget();
      } else {
        openWidget();
      }
    });

    close.addEventListener('click', closeWidget);

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && widget.classList.contains('open')) {
        closeWidget();
      }
    });

    // Trap focus inside widget when open
    widget.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && widget.classList.contains('open')) {
        const focusable = widget.querySelectorAll('button:not([disabled]), a[href]');
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    // Prevent widget from closing when clicking inside it
    widget.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  }

  // Font Size Controls
  function initFontSize() {
    let currentSize = loadPreference('fontSize') || 'normal';
    applyFontSize(currentSize);

    document.querySelectorAll('[data-action^="font-"]').forEach(btn => {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        
        const action = this.dataset.action;
        
        if (action === 'font-increase') {
          currentSize = currentSize === 'normal' ? 'large' : 'xlarge';
        } else if (action === 'font-decrease') {
          currentSize = currentSize === 'xlarge' ? 'large' : 'normal';
        } else if (action === 'font-reset') {
          currentSize = 'normal';
        }

        applyFontSize(currentSize);
        savePreference('fontSize', currentSize);
        
        const sizeText = currentSize === 'xlarge' ? 'extra large' : currentSize;
        announce(`Font size set to ${sizeText}`);
      });
    });
  }

  function applyFontSize(size) {
    document.body.classList.remove('a11y-font-large', 'a11y-font-xlarge');
    if (size === 'large') {
      document.body.classList.add('a11y-font-large');
    } else if (size === 'xlarge') {
      document.body.classList.add('a11y-font-xlarge');
    }
  }

  // Toggle Feature
  function initToggleFeature(action, className, label) {
    const isEnabled = loadPreference(action) === 'true';
    const btn = document.querySelector(`[data-action="${action}"]`);
    
    if (isEnabled) {
      document.body.classList.add(className);
      if (btn) btn.setAttribute('aria-pressed', 'true');
    }

    if (btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        
        const enabled = document.body.classList.toggle(className);
        this.setAttribute('aria-pressed', enabled.toString());
        savePreference(action, enabled.toString());
        announce(`${label} ${enabled ? 'enabled' : 'disabled'}`);
      });
    }
  }

  // High Contrast Mode
  function initHighContrast() {
    initToggleFeature('high-contrast', 'a11y-high-contrast', 'High contrast mode');
  }

  // Dark Mode / Invert Colors
  function initDarkMode() {
    initToggleFeature('dark-mode', 'a11y-dark-mode', 'Inverted colors');
  }

  // Readable Font
  function initReadableFont() {
    initToggleFeature('readable-font', 'a11y-readable-font', 'Readable font');
  }

  // Focus Mode
  function initFocusMode() {
    initToggleFeature('focus-mode', 'a11y-focus-mode', 'Focus highlights');
  }

  // Reset All Settings
  function initResetAll() {
    const resetBtn = document.querySelector('[data-action="reset-all"]');
    if (!resetBtn) return;

    resetBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Remove all classes
      document.body.classList.remove(
        'a11y-high-contrast',
        'a11y-dark-mode',
        'a11y-readable-font',
        'a11y-focus-mode',
        'a11y-font-large',
        'a11y-font-xlarge'
      );

      // Reset all buttons
      document.querySelectorAll('.a11y-btn[aria-pressed]').forEach(btn => {
        btn.setAttribute('aria-pressed', 'false');
      });

      // Clear localStorage
      try {
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith(STORAGE_PREFIX)) {
            localStorage.removeItem(key);
          }
        });
      } catch (e) {
        console.warn('Could not clear preferences:', e);
      }

      announce('All accessibility settings have been reset');
    });
  }

  // Keyboard Navigation Detection
  function initKeyboardDetection() {
    let isKeyboardUser = false;

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        isKeyboardUser = true;
        document.body.classList.add('keyboard-user');
      }
    });

    document.addEventListener('mousedown', () => {
      if (isKeyboardUser) {
        isKeyboardUser = false;
        document.body.classList.remove('keyboard-user');
      }
    });
  }

  // Enhanced Mobile Menu Accessibility
  function enhanceMobileMenu() {
    const hamburger = document.querySelector('.nav-hamburger');
    const mobileMenu = document.getElementById('mobile-menu');
    
    if (!hamburger || !mobileMenu) return;

    const originalHandler = hamburger.onclick;
    hamburger.onclick = function() {
      if (originalHandler) originalHandler.call(this);
      
      const isOpen = mobileMenu.classList.contains('open');
      hamburger.setAttribute('aria-expanded', isOpen.toString());
      mobileMenu.setAttribute('aria-hidden', (!isOpen).toString());
      
      if (isOpen) {
        const firstLink = mobileMenu.querySelector('.mobile-menu__link');
        if (firstLink) {
          setTimeout(() => firstLink.focus(), 100);
        }
        announce('Mobile menu opened');
      } else {
        announce('Mobile menu closed');
      }
    };
  }

  // Enhanced Cart Drawer Accessibility
  function enhanceCartDrawer() {
    const cartDrawer = document.getElementById('cart-drawer');
    const cartTriggers = document.querySelectorAll('[data-cart-trigger]');
    const cartClose = document.getElementById('cart-close');
    const cartOverlay = document.getElementById('cart-overlay');

    if (!cartDrawer) return;

    function updateCartAria(isOpen) {
      cartDrawer.setAttribute('aria-hidden', (!isOpen).toString());
      if (isOpen) {
        cartClose?.focus();
        announce('Shopping cart opened');
      } else {
        announce('Shopping cart closed');
      }
    }

    cartTriggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        setTimeout(() => updateCartAria(cartDrawer.classList.contains('open')), 50);
      });
    });

    cartClose?.addEventListener('click', () => {
      setTimeout(() => updateCartAria(false), 50);
      cartTriggers[0]?.focus();
    });

    cartOverlay?.addEventListener('click', () => {
      setTimeout(() => updateCartAria(false), 50);
      cartTriggers[0]?.focus();
    });

    // Trap focus in cart drawer
    cartDrawer.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && cartDrawer.classList.contains('open')) {
        const focusable = cartDrawer.querySelectorAll(
          'button:not([disabled]), a[href], input:not([disabled])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }

      if (e.key === 'Escape' && cartDrawer.classList.contains('open')) {
        cartClose?.click();
      }
    });

    // Announce quantity changes
    document.querySelectorAll('.qty-btn').forEach(btn => {
      btn.addEventListener('click', function() {
        setTimeout(() => {
          const qtyDisplay = this.closest('.cart-item__qty')?.querySelector('.qty-val');
          if (qtyDisplay) {
            const quantity = qtyDisplay.textContent;
            announce(`Quantity updated to ${quantity}`, 'assertive');
          }
        }, 100);
      });
    });
  }

  // Skip Link Functionality
  function initSkipLinks() {
    document.querySelectorAll('.skip-to-content').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const target = document.querySelector(link.getAttribute('href'));
        if (target) {
          target.focus();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          announce(`Skipped to ${link.textContent.toLowerCase()}`);
        }
      });
    });
  }

  // Form Validation Announcements
  function enhanceFormValidation() {
    document.querySelectorAll('input, textarea').forEach(field => {
      field.addEventListener('invalid', (e) => {
        e.preventDefault();
        const label = document.querySelector(`label[for="${field.id}"]`)?.textContent || 'This field';
        announce(`${label}: ${field.validationMessage}`, 'assertive');
      });
    });
  }

  // Image Alt Text Warning (Development)
  function checkImageAltText() {
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('myshopify.com')) {
      document.querySelectorAll('img:not([alt])').forEach(img => {
        console.warn('Image missing alt text:', img.src);
      });
    }
  }

  // Initialize All Accessibility Features
  document.addEventListener('DOMContentLoaded', () => {
    initA11yWidget();
    initFontSize();
    initHighContrast();
    initDarkMode();
    initReadableFont();
    initFocusMode();
    initResetAll();
    initKeyboardDetection();
    enhanceMobileMenu();
    enhanceCartDrawer();
    initSkipLinks();
    enhanceFormValidation();
    checkImageAltText();

    // Announce page ready for screen readers
    setTimeout(() => {
      announce('Page loaded. Press Tab to navigate.');
    }, 1000);
  });

  // Detect system preferences
  if (window.matchMedia) {
    // Reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (prefersReducedMotion.matches) {
      document.body.classList.add('prefers-reduced-motion');
    }

    // High contrast
    const prefersHighContrast = window.matchMedia('(prefers-contrast: high)');
    if (prefersHighContrast.matches && !loadPreference('high-contrast')) {
      document.body.classList.add('a11y-high-contrast');
    }

    // Dark mode system preference (optional)
    const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)');
    if (prefersDarkMode.matches && !loadPreference('dark-mode')) {
      // Optional: auto-enable dark mode
      // document.body.classList.add('a11y-dark-mode');
    }
  }

})();
